import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle, Check, ClipboardCheck, Minus, Sparkles } from 'lucide-react';
import { useReducedMotion, type Variants } from 'motion/react';
import * as m from 'motion/react-m';
import { useIntl } from 'react-intl';

/** A section that was written into (or left unchanged in) the EHR note. */
export interface ComputerUseNotePushRecapSection {
  sectionHeading: string;
}

/** A section the agent could not place, with the machine reason. */
export interface ComputerUseNotePushRecapFailure {
  sectionHeading: string;
  reason: string;
}

export type ComputerUseNotePushRecapCardStatus = 'generating' | 'completed';

export interface ComputerUseNotePushRecapCardProps {
  status: ComputerUseNotePushRecapCardStatus;
  title: string;
  recorded: ComputerUseNotePushRecapSection[];
  keptUnchanged: ComputerUseNotePushRecapSection[];
  failed: ComputerUseNotePushRecapFailure[];
}

const listVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.045, delayChildren: 0.06 } },
};

const rowVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const SkeletonRow = ({ widthClass }: { widthClass: string }) => (
  <div className="flex items-center gap-3">
    <div className="size-3.5 shrink-0 animate-pulse rounded-full bg-surface-page" />
    <div className={cn('h-3 animate-pulse rounded bg-surface-page', widthClass)} />
  </div>
);

/**
 * Per-field recap for a completed `record_note_push_hints` call: the sections
 * the agent recorded into the EHR, the ones it kept unchanged, and the ones it
 * failed to place (with the reason). Mirrors {@link FormFillCard}'s animated
 * checklist — recorded rows get a success check, kept-unchanged rows a muted
 * dash, failures a destructive alert + reason.
 */
export const ComputerUseNotePushRecapCard = ({
  status,
  title,
  recorded,
  keptUnchanged,
  failed,
}: ComputerUseNotePushRecapCardProps) => {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();

  if (status === 'generating') {
    return (
      <div
        className="flex w-full flex-col gap-3 rounded-xl border border-border bg-surface-accent p-4"
        data-testid="note-push-recap-card"
        data-status="generating"
      >
        <div className="flex items-center gap-3">
          <m.span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-page text-text-secondary"
            animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkles className="size-4" />
          </m.span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <TypographyP2Bold className="truncate text-text-primary">
              {title}
            </TypographyP2Bold>
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage({
                id: '+Lmt0oIx9t',
                defaultMessage: 'Recording note mapping…',
              })}
            </TypographyCaption>
          </div>
        </div>
        <div className="flex flex-col gap-2.5 pl-11">
          <SkeletonRow widthClass="w-40" />
          <SkeletonRow widthClass="w-28" />
        </div>
      </div>
    );
  }

  const recordedCount = recorded.length;
  const failedCount = failed.length;
  const hasFailures = failedCount > 0;

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-xl border border-border bg-surface-accent p-4"
      data-testid="note-push-recap-card"
      data-status="completed"
    >
      <div className="flex items-center gap-3">
        <m.span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-lg',
            hasFailures
              ? 'bg-validation-warning-muted text-validation-warning'
              : 'bg-validation-success-muted text-validation-success'
          )}
          initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 24 }}
        >
          <ClipboardCheck className="size-4" />
        </m.span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyPBold className="truncate text-text-primary">
            {title}
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage(
                {
                  id: '23E5JUHrtU',
                  defaultMessage:
                    '{recorded, plural, one {# section recorded} other {# sections recorded}}',
                },
                { recorded: recordedCount }
              )}
            </TypographyCaption>
            {hasFailures && (
              <TypographyCaption className="text-validation-destructive">
                {intl.formatMessage(
                  {
                    id: '4DUBBOWgAE',
                    defaultMessage: '· {failed, plural, one {# failed} other {# failed}}',
                  },
                  { failed: failedCount }
                )}
              </TypographyCaption>
            )}
          </div>
        </div>
      </div>

      <m.ul
        className="flex flex-col divide-y divide-border/70 rounded-lg bg-surface-page/60"
        {...(reduceMotion
          ? {}
          : { variants: listVariants, initial: 'hidden', animate: 'visible' })}
      >
        {recorded.map((section, index) => (
          <m.li
            key={`recorded-${section.sectionHeading}-${index}`}
            className="flex items-center gap-3 px-3 py-2"
            data-testid="note-push-recap-recorded"
            {...(reduceMotion ? {} : { variants: rowVariants })}
          >
            <Check
              className="size-3.5 shrink-0 text-validation-success"
              aria-hidden="true"
            />
            <TypographyP2Bold className="min-w-0 flex-1 truncate text-text-primary">
              {section.sectionHeading}
            </TypographyP2Bold>
          </m.li>
        ))}
        {keptUnchanged.map((section, index) => (
          <m.li
            key={`kept-${section.sectionHeading}-${index}`}
            className="flex items-center gap-3 px-3 py-2"
            data-testid="note-push-recap-kept"
            {...(reduceMotion ? {} : { variants: rowVariants })}
          >
            <Minus className="size-3.5 shrink-0 text-text-tertiary" aria-hidden="true" />
            <TypographyP2 className="min-w-0 flex-1 truncate text-text-secondary">
              {section.sectionHeading}
            </TypographyP2>
            <TypographyCaption className="shrink-0 text-text-tertiary">
              {intl.formatMessage({ id: '6kdJPpGI0x', defaultMessage: 'Unchanged' })}
            </TypographyCaption>
          </m.li>
        ))}
        {failed.map((failure, index) => (
          <m.li
            key={`failed-${failure.sectionHeading}-${index}`}
            className="flex items-start gap-3 px-3 py-2"
            data-testid="note-push-recap-failed"
            {...(reduceMotion ? {} : { variants: rowVariants })}
          >
            <AlertTriangle
              className="mt-0.5 size-3.5 shrink-0 text-validation-destructive"
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <TypographyP2Bold className="truncate text-text-primary">
                {failure.sectionHeading}
              </TypographyP2Bold>
              <TypographyCaption className="text-validation-destructive">
                {failure.reason}
              </TypographyCaption>
            </div>
          </m.li>
        ))}
      </m.ul>
    </div>
  );
};
